/**
 * AxLabs Physics Experiments - Oscillations
 *
 * Collection of oscillatory motion experiments generated using
 * the declarative framework.
 */
import { createExperimentFromSchema, STANDARD_GRAVITY } from '../../framework/index.js';
import { getParam, getState, getStateValues } from './helpers.js';
// ============================================================================
// UNDERDAMPED OSCILLATOR
// ============================================================================
export const UnderdampedOscillator = createExperimentFromSchema({
    id: 'underdamped-oscillator',
    name: 'Underdamped Harmonic Oscillator',
    domain: 'physics',
    description: 'Mass-spring system with light damping that oscillates with decaying amplitude',
    conceptTags: ['Damped Oscillations', 'Exponential Decay', 'Quality Factor'],
    difficulty: 4,
    stateVariables: [
        { id: 'x', name: 'Displacement', unit: 'm', initial: 1, symbol: 'x', description: 'Position from equilibrium' },
        { id: 'v', name: 'Velocity', unit: 'm/s', initial: 0, symbol: 'v' },
    ],
    parameters: [
        { id: 'm', name: 'Mass', unit: 'kg', min: 0.1, max: 10, default: 1, symbol: 'm' },
        { id: 'k', name: 'Spring Constant', unit: 'N/m', min: 1, max: 100, default: 20, symbol: 'k' },
        { id: 'b', name: 'Damping Coefficient', unit: 'kg/s', min: 0.01, max: 5, default: 0.5, symbol: 'b' },
    ],
    equations: [
        {
            id: 'equation',
            name: 'Equation of Motion',
            latex: 'm\\frac{d^2x}{dt^2} + b\\frac{dx}{dt} + kx = 0',
            description: 'Differential equation for damped harmonic oscillator',
        },
        {
            id: 'solution',
            name: 'Underdamped Solution',
            latex: 'x(t) = A e^{-\\gamma t}\\cos(\\omega_d t + \\phi)',
            description: 'Solution when damping is less than critical',
        },
        {
            id: 'damped-freq',
            name: 'Damped Frequency',
            latex: '\\omega_d = \\sqrt{\\omega_0^2 - \\gamma^2}',
            description: 'Actual oscillation frequency with damping',
        },
    ],
    derivatives: (state, params) => {
        const m = getParam(params, 'm');
        const k = getParam(params, 'k');
        const b = getParam(params, 'b');
        const x = getState(state, 'x');
        const v = getState(state, 'v');
        const omega0_sq = k / m;
        const gamma = b / (2 * m);
        return {
            x: v,
            v: -2 * gamma * v - omega0_sq * x,
        };
    },
    derivedState: (_state, params) => {
        const m = getParam(params, 'm');
        const k = getParam(params, 'k');
        const b = getParam(params, 'b');
        const omega0 = Math.sqrt(k / m);
        const gamma = b / (2 * m);
        const omega_d = Math.sqrt(Math.max(0, omega0 * omega0 - gamma * gamma));
        const Q = omega0 / (2 * gamma);
        return {
            natural_frequency: omega0,
            damped_frequency: omega_d,
            damping_ratio: gamma / omega0,
            quality_factor: Q,
        };
    },
    energyComputation: (state, params) => {
        const m = getParam(params, 'm');
        const k = getParam(params, 'k');
        const x = getState(state, 'x');
        const v = getState(state, 'v');
        const ke = 0.5 * m * v * v;
        const pe = 0.5 * k * x * x;
        const total = ke + pe;
        return { kinetic: ke, potential: pe, thermal: 0, total };
    },
    failureModes: [
        {
            id: 'overdamped',
            name: 'System Not Underdamped',
            check: (_state, params) => {
                const m = getParam(params, 'm');
                const k = getParam(params, 'k');
                const b = getParam(params, 'b');
                const gamma = b / (2 * m);
                const omega0 = Math.sqrt(k / m);
                return gamma >= omega0;
            },
            severity: 'warning',
            educationalMessage: 'The system is not underdamped. Reduce damping (b) or increase the spring constant (k) to see oscillations.',
        },
    ],
});
// ============================================================================
// CRITICALLY DAMPED OSCILLATOR
// ============================================================================
export const CriticallyDampedOscillator = createExperimentFromSchema({
    id: 'critically-damped-oscillator',
    name: 'Critically Damped Oscillator',
    domain: 'physics',
    description: 'Mass-spring system with critical damping - fastest return to equilibrium without oscillation',
    conceptTags: ['Critical Damping', 'Transient Response', 'Control Systems'],
    difficulty: 5,
    stateVariables: [
        { id: 'x', name: 'Displacement', unit: 'm', initial: 1, symbol: 'x' },
        { id: 'v', name: 'Velocity', unit: 'm/s', initial: 0, symbol: 'v' },
    ],
    parameters: [
        { id: 'm', name: 'Mass', unit: 'kg', min: 0.1, max: 10, default: 1, symbol: 'm' },
        { id: 'k', name: 'Spring Constant', unit: 'N/m', min: 1, max: 100, default: 25, symbol: 'k' },
        { id: 'b', name: 'Damping Coefficient', unit: 'kg/s', min: 0.1, max: 20, default: 10, symbol: 'b' },
    ],
    equations: [
        {
            id: 'critical-damping',
            name: 'Critical Damping Condition',
            latex: 'b_c = 2\\sqrt{mk}',
            description: 'Critical damping coefficient',
        },
        {
            id: 'solution',
            name: 'Solution',
            latex: 'x(t) = (A + Bt)e^{-\\omega_0 t}',
            description: 'Non-oscillatory solution',
        },
    ],
    derivatives: (state, params) => {
        const m = getParam(params, 'm');
        const k = getParam(params, 'k');
        const b = getParam(params, 'b');
        const x = getState(state, 'x');
        const v = getState(state, 'v');
        const omega0_sq = k / m;
        const gamma = b / (2 * m);
        return {
            x: v,
            v: -2 * gamma * v - omega0_sq * x,
        };
    },
    derivedState: (_state, params) => {
        const m = getParam(params, 'm');
        const k = getParam(params, 'k');
        const b = getParam(params, 'b');
        const omega0 = Math.sqrt(k / m);
        const b_critical = 2 * Math.sqrt(m * k);
        const damping_ratio = b / b_critical;
        return {
            natural_frequency: omega0,
            critical_damping: b_critical,
            damping_ratio,
        };
    },
    energyComputation: (state, params) => {
        const m = getParam(params, 'm');
        const k = getParam(params, 'k');
        const x = getState(state, 'x');
        const v = getState(state, 'v');
        const ke = 0.5 * m * v * v;
        const pe = 0.5 * k * x * x;
        return { kinetic: ke, potential: pe, thermal: 0, total: ke + pe };
    },
    failureModes: [
        {
            id: 'not-critical',
            name: 'Not Critically Damped',
            check: (_state, params) => {
                const m = getParam(params, 'm');
                const k = getParam(params, 'k');
                const b = getParam(params, 'b');
                const b_c = 2 * Math.sqrt(m * k);
                return Math.abs(b - b_c) > 0.1 * b_c;
            },
            severity: 'warning',
            educationalMessage: 'The system is not exactly at critical damping. b_critical = 2√(mk). Adjust b to match this value for optimal response.',
        },
    ],
});
// ============================================================================
// OVERDAMPED OSCILLATOR
// ============================================================================
export const OverdampedOscillator = createExperimentFromSchema({
    id: 'overdamped-oscillator',
    name: 'Overdamped Oscillator',
    domain: 'physics',
    description: 'Mass-spring system with heavy damping that slowly returns to equilibrium',
    conceptTags: ['Overdamping', 'Exponential Decay', 'Time Constants'],
    difficulty: 4,
    stateVariables: [
        { id: 'x', name: 'Displacement', unit: 'm', initial: 1, symbol: 'x' },
        { id: 'v', name: 'Velocity', unit: 'm/s', initial: 0, symbol: 'v' },
    ],
    parameters: [
        { id: 'm', name: 'Mass', unit: 'kg', min: 0.1, max: 10, default: 1, symbol: 'm' },
        { id: 'k', name: 'Spring Constant', unit: 'N/m', min: 1, max: 100, default: 10, symbol: 'k' },
        { id: 'b', name: 'Damping Coefficient', unit: 'kg/s', min: 5, max: 30, default: 15, symbol: 'b' },
    ],
    equations: [
        {
            id: 'solution',
            name: 'Overdamped Solution',
            latex: 'x(t) = A e^{r_1 t} + B e^{r_2 t}',
            description: 'Sum of two decaying exponentials',
        },
        {
            id: 'roots',
            name: 'Characteristic Roots',
            latex: 'r_{1,2} = -\\gamma \\pm \\sqrt{\\gamma^2 - \\omega_0^2}',
            description: 'Two distinct real roots for overdamped case',
        },
    ],
    derivatives: (state, params) => {
        const m = getParam(params, 'm');
        const k = getParam(params, 'k');
        const b = getParam(params, 'b');
        const x = getState(state, 'x');
        const v = getState(state, 'v');
        const omega0_sq = k / m;
        const gamma = b / (2 * m);
        return {
            x: v,
            v: -2 * gamma * v - omega0_sq * x,
        };
    },
    derivedState: (_state, params) => {
        const m = getParam(params, 'm');
        const k = getParam(params, 'k');
        const b = getParam(params, 'b');
        const omega0 = Math.sqrt(k / m);
        const gamma = b / (2 * m);
        const discriminant = gamma * gamma - omega0 * omega0;
        const r1 = -gamma + Math.sqrt(discriminant);
        const r2 = -gamma - Math.sqrt(discriminant);
        return {
            root1: r1,
            root2: r2,
            time_constant1: -1 / r1,
            time_constant2: -1 / r2,
        };
    },
    energyComputation: (state, params) => {
        const m = getParam(params, 'm');
        const k = getParam(params, 'k');
        const x = getState(state, 'x');
        const v = getState(state, 'v');
        const ke = 0.5 * m * v * v;
        const pe = 0.5 * k * x * x;
        return { kinetic: ke, potential: pe, thermal: 0, total: ke + pe };
    },
    failureModes: [
        {
            id: 'not-overdamped',
            name: 'Not Overdamped',
            check: (_state, params) => {
                const m = getParam(params, 'm');
                const k = getParam(params, 'k');
                const b = getParam(params, 'b');
                const gamma = b / (2 * m);
                const omega0 = Math.sqrt(k / m);
                return gamma <= omega0;
            },
            severity: 'warning',
            educationalMessage: 'The system is not overdamped. Increase the damping coefficient b.',
        },
    ],
});
// ============================================================================
// FORCED OSCILLATION WITH RESONANCE
// ============================================================================
export const ForcedOscillation = createExperimentFromSchema({
    id: 'forced-oscillation',
    name: 'Forced Oscillation with Resonance',
    domain: 'physics',
    description: 'Driven harmonic oscillator showing resonance phenomenon',
    conceptTags: ['Resonance', 'Driving Force', 'Frequency Response', 'Phase Shift'],
    difficulty: 6,
    stateVariables: [
        { id: 'x', name: 'Displacement', unit: 'm', initial: 0, symbol: 'x' },
        { id: 'v', name: 'Velocity', unit: 'm/s', initial: 0, symbol: 'v' },
    ],
    parameters: [
        { id: 'm', name: 'Mass', unit: 'kg', min: 0.1, max: 10, default: 1, symbol: 'm' },
        { id: 'k', name: 'Spring Constant', unit: 'N/m', min: 1, max: 100, default: 25, symbol: 'k' },
        { id: 'b', name: 'Damping Coefficient', unit: 'kg/s', min: 0.01, max: 2, default: 0.5, symbol: 'b' },
        { id: 'F0', name: 'Driving Amplitude', unit: 'N', min: 0.1, max: 10, default: 2, symbol: 'F_0' },
        { id: 'omega_drive', name: 'Driving Frequency', unit: 'rad/s', min: 0.1, max: 20, default: 5, symbol: '\\omega_d' },
    ],
    equations: [
        {
            id: 'forced-eq',
            name: 'Forced Oscillator Equation',
            latex: 'm\\ddot{x} + b\\dot{x} + kx = F_0\\cos(\\omega_d t)',
            description: 'Driven damped harmonic oscillator',
        },
        {
            id: 'amplitude',
            name: 'Steady-State Amplitude',
            latex: 'A = \\frac{F_0/m}{\\sqrt{(\\omega_0^2 - \\omega_d^2)^2 + (2\\gamma\\omega_d)^2}}',
            description: 'Amplitude of steady-state response',
        },
        {
            id: 'phase',
            name: 'Phase Lag',
            latex: '\\tan\\phi = \\frac{2\\gamma\\omega_d}{\\omega_0^2 - \\omega_d^2}',
            description: 'Phase of response relative to drive',
        },
    ],
    derivatives: (state, params, time) => {
        const m = getParam(params, 'm');
        const k = getParam(params, 'k');
        const b = getParam(params, 'b');
        const F0 = getParam(params, 'F0');
        const omega_drive = getParam(params, 'omega_drive');
        const x = getState(state, 'x');
        const v = getState(state, 'v');
        const omega0_sq = k / m;
        const gamma = b / m;
        const drive = (F0 / m) * Math.cos(omega_drive * time);
        return {
            x: v,
            v: drive - 2 * gamma * v - omega0_sq * x,
        };
    },
    derivedState: (_state, params) => {
        const m = getParam(params, 'm');
        const k = getParam(params, 'k');
        const b = getParam(params, 'b');
        const F0 = getParam(params, 'F0');
        const omega_drive = getParam(params, 'omega_drive');
        const omega0 = Math.sqrt(k / m);
        const gamma = b / (2 * m);
        const omega_r = Math.sqrt(Math.max(0, omega0 * omega0 - 2 * gamma * gamma));
        const denom = Math.sqrt(Math.pow(omega0 * omega0 - omega_drive * omega_drive, 2) +
            Math.pow(2 * gamma * omega_drive, 2));
        const A_ss = (F0 / m) / denom;
        const phi = Math.atan2(2 * gamma * omega_drive, omega0 * omega0 - omega_drive * omega_drive);
        return {
            natural_frequency: omega0,
            resonance_frequency: omega_r,
            drive_frequency: omega_drive,
            steady_state_amplitude: A_ss,
            phase_lag: phi,
        };
    },
    energyComputation: (state, params) => {
        const m = getParam(params, 'm');
        const k = getParam(params, 'k');
        const x = getState(state, 'x');
        const v = getState(state, 'v');
        const ke = 0.5 * m * v * v;
        const pe = 0.5 * k * x * x;
        return { kinetic: ke, potential: pe, thermal: 0, total: ke + pe };
    },
    failureModes: [
        {
            id: 'at-resonance',
            name: 'Near Resonance',
            check: (_state, params) => {
                const m = getParam(params, 'm');
                const k = getParam(params, 'k');
                const omega_drive = getParam(params, 'omega_drive');
                const omega0 = Math.sqrt(k / m);
                return Math.abs(omega_drive - omega0) < 0.5;
            },
            severity: 'warning',
            educationalMessage: 'The driving frequency is close to the natural frequency. This is the resonance condition where amplitude is maximized.',
        },
    ],
});
// ============================================================================
// COUPLED PENDULUMS
// ============================================================================
export const CoupledPendulums = createExperimentFromSchema({
    id: 'coupled-pendulums',
    name: 'Coupled Pendulums',
    domain: 'physics',
    description: 'Two pendulums connected by a weak spring demonstrating normal modes',
    conceptTags: ['Normal Modes', 'Coupled Oscillations', 'Energy Transfer', 'Beats'],
    difficulty: 7,
    stateVariables: [
        { id: 'theta1', name: 'Pendulum 1 Angle', unit: 'rad', initial: 0.15, symbol: '\\theta_1' },
        { id: 'theta2', name: 'Pendulum 2 Angle', unit: 'rad', initial: 0, symbol: '\\theta_2' },
        { id: 'omega1', name: 'Pendulum 1 Angular Velocity', unit: 'rad/s', initial: 0, symbol: '\\omega_1' },
        { id: 'omega2', name: 'Pendulum 2 Angular Velocity', unit: 'rad/s', initial: 0, symbol: '\\omega_2' },
    ],
    parameters: [
        { id: 'm', name: 'Mass', unit: 'kg', min: 0.1, max: 5, default: 1, symbol: 'm' },
        { id: 'L', name: 'Length', unit: 'm', min: 0.1, max: 3, default: 1, symbol: 'L' },
        { id: 'k_c', name: 'Coupling Spring Constant', unit: 'N/m', min: 0, max: 50, default: 5, symbol: 'k_c' },
        { id: 'd', name: 'Separation Distance', unit: 'm', min: 0.5, max: 3, default: 1.5, symbol: 'd' },
        { id: 'b', name: 'Damping', unit: 'kg/s', min: 0, max: 0.5, default: 0.02, symbol: 'b' },
        { id: 'g', name: 'Gravity', unit: 'm/s²', min: 1, max: 20, default: STANDARD_GRAVITY, symbol: 'g' },
    ],
    equations: [
        {
            id: 'normal-mode-symmetric',
            name: 'Symmetric Mode Frequency',
            latex: '\\omega_+ = \\sqrt{\\frac{g}{L}}',
            description: 'Both pendulums swing together (spring unstretched)',
        },
        {
            id: 'normal-mode-antisymmetric',
            name: 'Antisymmetric Mode Frequency',
            latex: '\\omega_- = \\sqrt{\\frac{g}{L} + \\frac{2k_c}{m}}',
            description: 'Pendulums swing in opposite directions',
        },
    ],
    derivatives: (state, params) => {
        const g = getParam(params, 'g');
        const L = getParam(params, 'L');
        const m = getParam(params, 'm');
        const k_c = getParam(params, 'k_c');
        const d = getParam(params, 'd');
        const b = getParam(params, 'b');
        const theta1 = getState(state, 'theta1');
        const theta2 = getState(state, 'theta2');
        const omega1 = getState(state, 'omega1');
        const omega2 = getState(state, 'omega2');
        const coupling_term = (k_c * d / (m * L * L)) * (theta2 - theta1);
        const gravity_term = g / L;
        return {
            theta1: omega1,
            theta2: omega2,
            omega1: -gravity_term * theta1 + coupling_term - (b / m) * omega1,
            omega2: -gravity_term * theta2 - coupling_term - (b / m) * omega2,
        };
    },
    derivedState: (state, params) => {
        const g = getParam(params, 'g');
        const L = getParam(params, 'L');
        const m = getParam(params, 'm');
        const k_c = getParam(params, 'k_c');
        const theta1 = getState(state, 'theta1');
        const theta2 = getState(state, 'theta2');
        const omega0 = Math.sqrt(g / L);
        const omega_minus = Math.sqrt(g / L + 2 * k_c / m);
        const theta_plus = (theta1 + theta2) / 2;
        const theta_minus = (theta1 - theta2) / 2;
        return {
            symmetric_mode_freq: omega0,
            antisymmetric_mode_freq: omega_minus,
            beat_frequency: omega_minus - omega0,
            symmetric_coord: theta_plus,
            antisymmetric_coord: theta_minus,
        };
    },
    energyComputation: (state, params) => {
        const m = getParam(params, 'm');
        const L = getParam(params, 'L');
        const g = getParam(params, 'g');
        const k_c = getParam(params, 'k_c');
        const d = getParam(params, 'd');
        const theta1 = getState(state, 'theta1');
        const theta2 = getState(state, 'theta2');
        const omega1 = getState(state, 'omega1');
        const omega2 = getState(state, 'omega2');
        const ke = 0.5 * m * L * L * (omega1 * omega1 + omega2 * omega2);
        const pe_grav = m * g * L * (0.5 * theta1 * theta1 + 0.5 * theta2 * theta2);
        const x_spring = d * (theta2 - theta1);
        const pe_spring = 0.5 * k_c * x_spring * x_spring;
        return {
            kinetic: ke,
            potential: pe_grav + pe_spring,
            thermal: 0,
            total: ke + pe_grav + pe_spring,
        };
    },
    bounds: {
        theta1: { min: -0.5, max: 0.5 },
        theta2: { min: -0.5, max: 0.5 },
        omega1: { min: -10, max: 10 },
        omega2: { min: -10, max: 10 },
    },
});
// ============================================================================
// COUPLED SPRING-MASS SYSTEM
// ============================================================================
export const CoupledSpringMass = createExperimentFromSchema({
    id: 'coupled-spring-mass',
    name: 'Coupled Spring-Mass System',
    domain: 'physics',
    description: 'Two masses connected in series with springs demonstrating normal modes',
    conceptTags: ['Normal Modes', 'Coupled Oscillations', 'Matrix Methods'],
    difficulty: 6,
    stateVariables: [
        { id: 'x1', name: 'Mass 1 Displacement', unit: 'm', initial: 0.5, symbol: 'x_1' },
        { id: 'x2', name: 'Mass 2 Displacement', unit: 'm', initial: 0, symbol: 'x_2' },
        { id: 'v1', name: 'Mass 1 Velocity', unit: 'm/s', initial: 0, symbol: 'v_1' },
        { id: 'v2', name: 'Mass 2 Velocity', unit: 'm/s', initial: 0, symbol: 'v_2' },
    ],
    parameters: [
        { id: 'm1', name: 'Mass 1', unit: 'kg', min: 0.1, max: 10, default: 1, symbol: 'm_1' },
        { id: 'm2', name: 'Mass 2', unit: 'kg', min: 0.1, max: 10, default: 1, symbol: 'm_2' },
        { id: 'k1', name: 'Spring 1 Constant', unit: 'N/m', min: 1, max: 100, default: 20, symbol: 'k_1' },
        { id: 'k2', name: 'Spring 2 Constant', unit: 'N/m', min: 1, max: 100, default: 15, symbol: 'k_2' },
        { id: 'k3', name: 'Spring 3 Constant', unit: 'N/m', min: 1, max: 100, default: 10, symbol: 'k_3' },
        { id: 'b', name: 'Damping', unit: 'kg/s', min: 0, max: 1, default: 0.05, symbol: 'b' },
    ],
    derivatives: (state, params) => {
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const k1 = getParam(params, 'k1');
        const k2 = getParam(params, 'k2');
        const k3 = getParam(params, 'k3');
        const b = getParam(params, 'b');
        const x1 = getState(state, 'x1');
        const x2 = getState(state, 'x2');
        const v1 = getState(state, 'v1');
        const v2 = getState(state, 'v2');
        const F1 = -k1 * x1 + k2 * (x2 - x1) - b * v1;
        const F2 = -k2 * (x2 - x1) - k3 * x2 - b * v2;
        return {
            x1: v1,
            x2: v2,
            v1: F1 / m1,
            v2: F2 / m2,
        };
    },
    derivedState: (state, params) => {
        const m = getParam(params, 'm1');
        const k = getParam(params, 'k1');
        const k_c = getParam(params, 'k2');
        const x1 = getState(state, 'x1');
        const x2 = getState(state, 'x2');
        const omega1 = Math.sqrt(k / m);
        const omega2_approx = Math.sqrt((k + 2 * k_c + getParam(params, 'k3')) / m);
        const x_cm = (getParam(params, 'm1') * x1 + getParam(params, 'm2') * x2) / (getParam(params, 'm1') + getParam(params, 'm2'));
        const x_rel = x2 - x1;
        return {
            normal_mode_1_freq: omega1,
            normal_mode_2_freq: omega2_approx,
            center_of_mass: x_cm,
            relative_displacement: x_rel,
        };
    },
    energyComputation: (state, params) => {
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const k1 = getParam(params, 'k1');
        const k2 = getParam(params, 'k2');
        const k3 = getParam(params, 'k3');
        const x1 = getState(state, 'x1');
        const x2 = getState(state, 'x2');
        const v1 = getState(state, 'v1');
        const v2 = getState(state, 'v2');
        const ke = 0.5 * m1 * v1 * v1 + 0.5 * m2 * v2 * v2;
        const pe1 = 0.5 * k1 * x1 * x1;
        const pe2 = 0.5 * k2 * Math.pow(x2 - x1, 2);
        const pe3 = 0.5 * k3 * x2 * x2;
        return {
            kinetic: ke,
            potential: pe1 + pe2 + pe3,
            thermal: 0,
            total: ke + pe1 + pe2 + pe3,
        };
    },
    bounds: {
        x1: { min: -2, max: 2 },
        x2: { min: -2, max: 2 },
        v1: { min: -5, max: 5 },
        v2: { min: -5, max: 5 },
    },
});
// ============================================================================
// TORSIONAL PENDULUM
// ============================================================================
export const TorsionalPendulum = createExperimentFromSchema({
    id: 'torsional-pendulum',
    name: 'Torsional Pendulum',
    domain: 'physics',
    description: 'Rotational oscillations of a disk suspended by a wire',
    conceptTags: ['Rotational Dynamics', 'Torsion', 'Moment of Inertia'],
    difficulty: 5,
    stateVariables: [
        { id: 'theta', name: 'Angular Displacement', unit: 'rad', initial: 0.3, symbol: '\\theta' },
        { id: 'omega', name: 'Angular Velocity', unit: 'rad/s', initial: 0, symbol: '\\omega' },
    ],
    parameters: [
        { id: 'kappa', name: 'Torsion Constant', unit: 'N·m/rad', min: 0.001, max: 1, default: 0.01, symbol: '\\kappa' },
        { id: 'I', name: 'Moment of Inertia', unit: 'kg·m²', min: 0.0001, max: 0.1, default: 0.001, symbol: 'I' },
        { id: 'b', name: 'Rotational Damping', unit: 'kg·m²/s', min: 0, max: 0.01, default: 0.0001, symbol: 'b' },
    ],
    equations: [
        {
            id: 'equation',
            name: 'Equation of Motion',
            latex: 'I\\ddot{\\theta} + b\\dot{\\theta} + \\kappa\\theta = 0',
        },
        {
            id: 'period',
            name: 'Period',
            latex: 'T = 2\\pi\\sqrt{\\frac{I}{\\kappa}}',
        },
    ],
    derivatives: (state, params) => {
        const I = getParam(params, 'I');
        const kappa = getParam(params, 'kappa');
        const b = getParam(params, 'b');
        const theta = getState(state, 'theta');
        const omega = getState(state, 'omega');
        return {
            theta: omega,
            omega: -(kappa * theta + b * omega) / I,
        };
    },
    derivedState: (_state, params) => {
        const I = getParam(params, 'I');
        const kappa = getParam(params, 'kappa');
        const b = getParam(params, 'b');
        const omega0 = Math.sqrt(kappa / I);
        const period = 2 * Math.PI / omega0;
        const gamma = b / (2 * I);
        const Q = gamma > 0 ? omega0 / (2 * gamma) : Infinity;
        return {
            natural_frequency: omega0,
            period,
            quality_factor: Q,
            damping_ratio: gamma / omega0,
        };
    },
    energyComputation: (state, params) => {
        const I = getParam(params, 'I');
        const kappa = getParam(params, 'kappa');
        const theta = getState(state, 'theta');
        const omega = getState(state, 'omega');
        const ke = 0.5 * I * omega * omega;
        const pe = 0.5 * kappa * theta * theta;
        return { kinetic: ke, potential: pe, thermal: 0, total: ke + pe };
    },
    bounds: {
        theta: { min: -1, max: 1 },
        omega: { min: -10, max: 10 },
    },
});
// ============================================================================
// PHYSICAL PENDULUM
// ============================================================================
export const PhysicalPendulum = createExperimentFromSchema({
    id: 'physical-pendulum',
    name: 'Physical Pendulum',
    domain: 'physics',
    description: 'Rigid body oscillating about a pivot point',
    conceptTags: ['Rotational Dynamics', 'Moment of Inertia', 'Parallel Axis Theorem'],
    difficulty: 5,
    stateVariables: [
        { id: 'theta', name: 'Angular Displacement', unit: 'rad', initial: 0.4, symbol: '\\theta' },
        { id: 'omega', name: 'Angular Velocity', unit: 'rad/s', initial: 0, symbol: '\\omega' },
    ],
    parameters: [
        { id: 'M', name: 'Mass', unit: 'kg', min: 0.1, max: 10, default: 1, symbol: 'M' },
        { id: 'L', name: 'Distance to CM', unit: 'm', min: 0.1, max: 2, default: 0.5, symbol: 'L' },
        { id: 'I_cm', name: 'I about CM', unit: 'kg·m²', min: 0.001, max: 1, default: 0.1, symbol: 'I_{cm}' },
        { id: 'b', name: 'Damping', unit: 'kg·m²/s', min: 0, max: 0.5, default: 0.05, symbol: 'b' },
        { id: 'g', name: 'Gravity', unit: 'm/s²', min: 1, max: 20, default: STANDARD_GRAVITY, symbol: 'g' },
    ],
    equations: [
        {
            id: 'equation',
            name: 'Equation of Motion',
            latex: 'I_{pivot}\\ddot{\\theta} + b\\dot{\\theta} + MgL\\sin\\theta = 0',
        },
        {
            id: 'period',
            name: 'Period (Small Angle)',
            latex: 'T = 2\\pi\\sqrt{\\frac{I_{pivot}}{MgL}}',
        },
    ],
    derivatives: (state, params) => {
        const M = getParam(params, 'M');
        const L = getParam(params, 'L');
        const I_cm = getParam(params, 'I_cm');
        const b = getParam(params, 'b');
        const g = getParam(params, 'g');
        const theta = getState(state, 'theta');
        const omega = getState(state, 'omega');
        const I_pivot = I_cm + M * L * L;
        const torque_gravity = -M * g * L * Math.sin(theta);
        const torque_damping = -b * omega;
        return {
            theta: omega,
            omega: (torque_gravity + torque_damping) / I_pivot,
        };
    },
    derivedState: (state, params) => {
        const M = getParam(params, 'M');
        const L = getParam(params, 'L');
        const I_cm = getParam(params, 'I_cm');
        const g = getParam(params, 'g');
        const theta = getState(state, 'theta');
        const I_pivot = I_cm + M * L * L;
        const omega0 = Math.sqrt(M * g * L / I_pivot);
        const period = 2 * Math.PI / omega0;
        const L_equivalent = I_pivot / (M * L);
        const amplitude = Math.abs(theta);
        const correction = 1 + amplitude * amplitude / 16;
        return {
            natural_frequency: omega0,
            period_small_angle: period,
            period_large_angle: period * correction,
            equivalent_simple_pendulum_length: L_equivalent,
        };
    },
    energyComputation: (state, params) => {
        const M = getParam(params, 'M');
        const L = getParam(params, 'L');
        const I_cm = getParam(params, 'I_cm');
        const g = getParam(params, 'g');
        const theta = getState(state, 'theta');
        const omega = getState(state, 'omega');
        const I_pivot = I_cm + M * L * L;
        const ke = 0.5 * I_pivot * omega * omega;
        const h = L * (1 - Math.cos(theta));
        const pe = M * g * h;
        return {
            kinetic: ke,
            potential: pe,
            thermal: 0,
            total: ke + pe,
        };
    },
    failureModes: [
        {
            id: 'large-angle',
            name: 'Large Angle Approximation',
            check: (state) => Math.abs(getState(getStateValues(state), 'theta')) > 0.3,
            severity: 'warning',
            educationalMessage: 'For angles > 0.3 rad, the small angle approximation becomes less accurate.',
        },
    ],
    bounds: {
        theta: { min: -1.5, max: 1.5 },
        omega: { min: -10, max: 10 },
    },
});
//# sourceMappingURL=oscillations.js.map